import { ReactNode, useState, useEffect } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { SimpleFooter } from './SimpleFooter';
import AdhaChat from '../chatbot/AdhaChat';
import { ChatErrorBoundary } from '../chatbot/ChatErrorBoundary';
import { LeasingStoreShortcut } from '../leasing-store/LeasingStoreShortcut';
import { PerformanceIndicator } from '../ui/PerformanceIndicator';
import { useLocation } from 'react-router-dom';
import { useLoading } from '../../context/LoadingContext';
import { PageLoadingIndicator } from '../ui/PageLoadingIndicator';
import { FloatingComponentsProvider } from '../../context/FloatingComponentsContext';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { isPageLoading } = useLoading();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isLeasingStorePage = location.pathname.startsWith('/leasing-store');
  const isProfilePage = ['/profile', '/company', '/institution', '/abonnement', '/business-profile'].includes(location.pathname) || 
                        location.pathname.startsWith('/financial-institution');
  
  // Optimisation: masquer l'indicateur après le premier chargement
  useEffect(() => {
    if (isInitialLoad) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isInitialLoad]);

  // Préchargement des ressources critiques pour la performance
  useEffect(() => {
    // Précharger les polices critiques
    const preloadFonts = () => {
      // Exemple de police - remplacez par vos vraies polices
      const fontUrls = [
        'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap'
      ];
      
      fontUrls.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = url;
        link.as = 'style';
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      });
    };

    // Optimisation du rendu initial
    if ('requestIdleCallback' in window) {
      requestIdleCallback(preloadFonts);
    } else {
      setTimeout(preloadFonts, 100);
    }
  }, []);
  
  return (
    <FloatingComponentsProvider>
      <div className="min-h-screen flex flex-col relative overflow-x-hidden">
        <Header />
        <main className="flex-grow relative">
          {children}
        </main>
        
        {/* Indicateur de performance (dev mode uniquement) */}
        {process.env.NODE_ENV === 'development' && <PerformanceIndicator />}
        
        <div className="floating-container space-y-4 items-end">
          {!isLeasingStorePage && <LeasingStoreShortcut />}
        </div>
        <ChatErrorBoundary>
          <AdhaChat />
        </ChatErrorBoundary>
        {(isPageLoading || isInitialLoad) ? (
          <PageLoadingIndicator />
        ) : (isLeasingStorePage || isProfilePage) ? (
          <SimpleFooter className="relative z-10 w-full footer-container" />
        ) : (
          <Footer className="relative z-10 w-full footer-container" />
        )}
      </div>
    </FloatingComponentsProvider>
  );
}